import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  RadioButton,
  RadioButtonGroup,
  RadioButtonTheme,
  Text,
  TextPriority,
} from 'wix-ui-tpa/cssVars';
import { ExperimentsConsts } from '../../../../../../consts/experiments';
import { type PaymentOption, BorderStyle } from '../../../../../../types/types';
import { getServiceSlotIdentifier } from '../../../../../../utils';
import { DiscountRuleName } from '../../../Discount/Discount';
import { ServicePaymentRadioButtonsHooks } from './dataHooks';
import type { PaymentFormatProps } from './ServicePaymentOptions';
import { classes, st } from './ServicePaymentRadioButtons.st.css';
import settingsParams from '../../../../settingsParams';
import { useSettings } from '@wix/tpa-settings/react';

export const ServicePaymentRadioButtons: React.FC<PaymentFormatProps> = ({
  service,
  paymentOptions,
  label,
  selectedId,
  onSelect,
  getPaymentOptionLabel,
  buildPaymentOptionId,
}) => {
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const isUnderline =
    useSettings().get(settingsParams.fieldsBorderStyle) ===
    BorderStyle.UNDERLINE;
  const theme = isUnderline ? RadioButtonTheme.Default : RadioButtonTheme.Box;

  const isUseNewBenefitsAPIUoUEnabled = experiments.enabled(
    ExperimentsConsts.UseNewBenefitsAPIUoU,
  );

  const getSubtext = (option: PaymentOption): string | undefined => {
    if (
      isUseNewBenefitsAPIUoUEnabled &&
      option.disabled &&
      option.disabledReason
    ) {
      return `${option.pricingDetails ?? ''} ${option.disabledReason}`.trim();
    }

    if (option.pricingDetails && typeof option.pricingDetails === 'string') {
      return option.pricingDetails;
    }

    return undefined;
  };

  const getSuffix = (option: PaymentOption): React.ReactNode => {
    const showDiscountAtSuffix =
      option.discountDetails?.showAtSuffix && !isMobile;

    if (showDiscountAtSuffix) {
      return <DiscountRuleName {...option.discountDetails!} align="end" />;
    }

    if (option.discountDetails) {
      return <DiscountRuleName {...option.discountDetails} align="start" />;
    }

    return undefined;
  };

  const groupName = `payment-options-${getServiceSlotIdentifier(
    service.nestedSlot,
  )}`;

  return (
    <div
      className={st(classes.root, { isMobile, isUnderline })}
      data-hook={`${
        ServicePaymentRadioButtonsHooks.RadioButtons
      }_${getServiceSlotIdentifier(service.nestedSlot)}`}
    >
      <Text className={classes.label} priority={TextPriority.secondary}>
        {label}
      </Text>
      <RadioButtonGroup
        name={groupName}
        value={selectedId}
        onChange={onSelect}
        withSpacing={!isUnderline}
        fluid
        theme={theme}
      >
        {paymentOptions.map((option) => {
          const optionValue = buildPaymentOptionId(option);
          const isDisabled = isUseNewBenefitsAPIUoUEnabled && option.disabled;

          return (
            <RadioButton
              key={optionValue}
              value={optionValue}
              label={getPaymentOptionLabel(option)}
              subtext={getSubtext(option)}
              suffix={getSuffix(option)}
              checked={selectedId === optionValue}
              disabled={isDisabled}
            />
          );
        })}
      </RadioButtonGroup>
    </div>
  );
};
